// Shared UI kit for both portals: small, accessible (WCAG 2.1 AA on core flows), responsive to 360 px.
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import "./styles.css";

type BtnProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "danger" | "ghost"; busy?: boolean; size?: "sm" | "md" | "lg"; icon?: IconName;
};

export function Button({ variant = "secondary", busy, children, disabled, className = "", size = "md", icon, ...rest }: BtnProps) {
  return (
    <button className={`ui-btn ui-btn-${variant} ${size !== "md" ? `ui-btn-${size}` : ""} ${className}`} disabled={disabled || busy}
            aria-busy={busy} {...rest}>
      {busy ? <Spinner small /> : icon ? <Icon name={icon} size={size === "sm" ? 14 : 16} /> : null}
      {children}
    </button>
  );
}

export function Field({ label, hint, error, children }: { label: string; hint?: string; error?: string; children: (id: string) => ReactNode }) {
  const id = useId();
  return (
    <div className="ui-field">
      <label htmlFor={id}>{label}</label>
      {children(id)}
      {hint && !error ? <small className="ui-hint">{hint}</small> : null}
      {error ? <small className="ui-error" role="alert">{error}</small> : null}
    </div>
  );
}

export function Input({ className = "", ...props }: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input className={`ui-input ${className}`} {...props} />;
}

export function Select({ options, ...rest }: React.SelectHTMLAttributes<HTMLSelectElement> & { options: { value: string; label: string }[] }) {
  return (
    <select className="ui-input" {...rest}>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

export function Card({ title, subtitle, actions, children, className = "", flush }: {
  title?: ReactNode; subtitle?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string; flush?: boolean;
}) {
  return (
    <section className={`ui-card ${flush ? "ui-card-flush" : ""} ${className}`}>
      {title || actions ? (
        <header className="ui-card-head">
          {title ? <div className="ui-card-titles"><h2>{title}</h2>{subtitle ? <p className="ui-card-sub">{subtitle}</p> : null}</div> : <span />}
          {actions ? <div className="ui-row">{actions}</div> : null}
        </header>
      ) : null}
      {subtitle && !title ? <p className="ui-card-sub">{subtitle}</p> : null}
      {children}
    </section>
  );
}

/** Page title with an optional one-line description and actions on the right. */
export function PageHeader({ title, description, actions, eyebrow }: {
  title: ReactNode; description?: ReactNode; actions?: ReactNode; eyebrow?: ReactNode;
}) {
  return (
    <header className="ui-page-head">
      <div>
        {eyebrow ? <span className="ui-eyebrow">{eyebrow}</span> : null}
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
      </div>
      {actions ? <div className="ui-row">{actions}</div> : null}
    </header>
  );
}

const TONE: Record<string, string> = {
  ACCEPTED: "green", active: "green", succeeded: "green", synced: "green", online: "green", approved: "green",
  NEEDS_REVIEW: "amber", NEEDS_INPUT: "amber", degraded: "amber", pending: "amber", running: "amber", accepted: "amber",
  invited: "amber", conflict: "amber", paused: "amber", creating: "amber",
  QUARANTINED: "red", FETCH_FAILED: "red", error: "red", failed: "red", rejected: "red", failing: "red", revoked: "red",
  offline: "grey", DUPLICATE: "grey", PURGED: "grey", detached: "grey",
};

/** Words people recognise for the pipeline's codes; anything else is shown in sentence case. */
const LABEL: Record<string, string> = {
  ACCEPTED: "Accepted", NEEDS_REVIEW: "Needs review", NEEDS_INPUT: "Needs input", QUARANTINED: "Held back",
  DUPLICATE: "Duplicate", FETCH_FAILED: "Download failed", PURGED: "Purged", RECEIVED: "Received", FETCHED: "Downloaded",
  VALIDATED: "Checked", UNKNOWN_SENDER: "Unknown sender", CLIENT_UNRESOLVED: "Client unknown", PASSWORD_REQUIRED: "Password needed",
  UNCERTAIN_RELEVANCE: "Unclear document", LOW_QUALITY: "Low quality", POSSIBLE_DUPLICATE: "Possible duplicate",
  NOT_FINANCIAL: "Not financial", UNSUPPORTED_TYPE: "Unsupported file", UNSAFE_ARCHIVE: "Unsafe archive", MALWARE: "Malware",
  CORRUPT: "Corrupt file", EMPTY: "Empty file", SPOOF_SUSPECTED: "Possible spoof", FETCH_EXHAUSTED: "No longer on server",
  EXACT: "Exact copy", NEAR: "Near copy", HUMAN_OVERRIDE: "Changed by staff", LLM_BUDGET_EXHAUSTED: "AI limit reached",
  STATEMENT_NOT_PROVEN: "Statement does not add up", UNKNOWN_LAYOUT: "Statement layout not known",
  // document types
  sales_invoice: "Sales invoice", purchase_invoice: "Purchase invoice", expense_bill: "Expense bill", credit_note: "Credit note",
  debit_note: "Debit note", receipt: "Receipt", payment_proof_upi: "UPI payment proof", bank_statement: "Bank statement",
  eway_bill: "E-way bill", einvoice: "E-invoice", delivery_challan: "Delivery challan", purchase_order: "Purchase order",
  quotation: "Quotation", gst_notice: "GST notice", gst_return_doc: "GST return", tds_certificate: "TDS certificate",
  salary_payroll: "Salary & payroll", rent_agreement: "Rent agreement", ledger_export: "Ledger", tally_export: "Tally export",
  sales_register: "Sales register", purchase_register: "Purchase register",
  other_financial: "Other financial", unknown: "Unknown", archive: "Zip archive",
  owner: "Owner", admin: "Admin", staff: "Staff", viewer: "Viewer",
};

export function humanize(value: string): string {
  if (LABEL[value]) return LABEL[value]!;
  const s = value.replaceAll("_", " ").toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function Badge({ value, tone }: { value: string; tone?: string }) {
  return <span className={`ui-badge ui-${tone ?? TONE[value] ?? "blue"}`}>{humanize(value)}</span>;
}

export function Spinner({ small }: { small?: boolean }) {
  return <span className={small ? "ui-spinner ui-spinner-sm" : "ui-spinner"} role="status" aria-label="Loading" />;
}

export function Loading() {
  return <div className="ui-loading"><Spinner /></div>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="ui-empty">{children}</p>;
}

export function ErrorBanner({ error }: { error: unknown }) {
  if (!error) return null;
  const msg = error instanceof Error ? error.message : String(error);
  return <div className="ui-banner ui-banner-error" role="alert"><Icon name="alert" size={18} />{msg}</div>;
}

export function Notice({ children, tone }: { children: ReactNode; tone?: "success" }) {
  return <div className={`ui-banner ${tone ? `ui-banner-${tone}` : ""}`}><Icon name={tone ? "check" : "info"} size={18} /><div>{children}</div></div>;
}

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  className?: string;
}

/** Table on wide screens, stacked cards below 680 px. */
export function Table<T>({ rows, columns, rowKey, onRowClick, selected, onToggle, focusIndex }: {
  rows: T[]; columns: Column<T>[]; rowKey: (r: T) => string; onRowClick?: (r: T) => void;
  selected?: Set<string>; onToggle?: (id: string) => void; focusIndex?: number;
}) {
  return (
    <div className="ui-table-wrap">
      <table className="ui-table">
        <thead>
          <tr>
            {onToggle ? (
              <th className="ui-sel">
                <input type="checkbox" aria-label="Select all" checked={rows.length > 0 && rows.every((r) => selected?.has(rowKey(r)))}
                       onChange={(e) => rows.forEach((r) => { const id = rowKey(r); if (e.target.checked !== !!selected?.has(id)) onToggle(id); })} />
              </th>
            ) : null}
            {columns.map((c) => <th key={c.key} className={c.className}>{c.header}</th>)}
            {onRowClick ? <th aria-hidden="true" style={{ width: 40 }} /> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const id = rowKey(r);
            return (
              <tr key={id} className={`${onRowClick ? "ui-clickable" : ""} ${focusIndex === i ? "ui-focus-row" : ""}`}
                  onClick={() => onRowClick?.(r)} aria-selected={selected?.has(id)}>
                {onToggle ? (
                  <td className="ui-sel" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" aria-label="Select row" checked={selected?.has(id) ?? false} onChange={() => onToggle(id)} />
                  </td>
                ) : null}
                {columns.map((c) => <td key={c.key} data-label={c.header} className={c.className}>{c.render(r)}</td>)}
                {onRowClick ? <td className="ui-go-cell" data-label="" style={{ width: 40 }}><span className="ui-row-go"><Icon name="chevron" size={16} /></span></td> : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** Month as a plain dropdown of recent months ("Sep 2026"), instead of the browser's month input. Value is YYYY-MM or "". */
export function MonthPicker({ value, onChange, anyLabel, months = 24, ...rest }: {
  value: string; onChange: (v: string) => void; anyLabel?: string; months?: number;
} & Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "value" | "onChange">) {
  const d = new Date();
  d.setDate(1);
  const opts: { value: string; label: string }[] = [];
  for (let i = 0; i < months; i++) {
    opts.push({ value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}` });
    d.setMonth(d.getMonth() - 1);
  }
  if (value && !opts.some((o) => o.value === value)) opts.push({ value, label: fmtPeriod(value) });
  return <Select {...rest} value={value} onChange={(e) => onChange(e.target.value)} options={[...(anyLabel ? [{ value: "", label: anyLabel }] : []), ...opts]} />;
}

/** Centred dialog. `size` sm (forms), md (default), lg (pickers and lists); `footer` holds the actions, right-aligned. */
export function Modal({ open, title, subtitle, onClose, children, wide, size, footer, icon }: {
  open: boolean; title: string; subtitle?: ReactNode; onClose: () => void; children: ReactNode; wide?: boolean;
  size?: "sm" | "md" | "lg" | "xl"; footer?: ReactNode; icon?: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  const sz = size ?? (wide ? "xl" : "md");
  return (
    <dialog ref={ref} className={`ui-modal ui-modal-${sz}`} onClose={onClose} aria-label={title}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      {open ? (
        <div className="ui-modal-inner">
          <header>
            {icon ? <span className="ui-modal-icon">{icon}</span> : null}
            <div className="ui-modal-titles"><h2>{title}</h2>{subtitle ? <p>{subtitle}</p> : null}</div>
            <Button variant="ghost" className="ui-icon-btn" onClick={onClose} aria-label="Close"><Icon name="close" size={18} /></Button>
          </header>
          <div className="ui-modal-body">{children}</div>
          {footer ? <footer className="ui-modal-foot">{footer}</footer> : null}
        </div>
      ) : null}
    </dialog>
  );
}

export function Tabs({ tabs, value, onChange }: {
  tabs: { value: string; label: string; count?: number }[]; value: string; onChange: (v: string) => void;
}) {
  return (
    <div className="ui-tabs" role="tablist">
      {tabs.map((t) => (
        <button key={t.value} role="tab" aria-selected={t.value === value} className={t.value === value ? "ui-tab ui-tab-on" : "ui-tab"}
                onClick={() => onChange(t.value)}>
          {t.label}{t.count ? <span className="ui-tab-n">{t.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

/** Pill filters ("All", "Needs review" ...), single choice. */
export function Chips<T extends string>({ options, value, onChange }: {
  options: { value: T; label: string; count?: number }[]; value: T; onChange: (v: T) => void;
}) {
  return (
    <div className="ui-chips" role="group">
      {options.map((o) => (
        <button key={o.value} type="button" className="ui-chip" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
          {o.label}{o.count != null ? <span className="ui-chip-n">{o.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

export function Segmented<T extends string>({ options, value, onChange, label }: {
  options: { value: T; label: string; icon?: IconName }[]; value: T; onChange: (v: T) => void; label?: string;
}) {
  return (
    <div className="ui-seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
          {o.icon ? <Icon name={o.icon} size={14} /> : null}{o.label}
        </button>
      ))}
    </div>
  );
}

/** A setting: switch, title and one-line explanation. */
export function SwitchRow({ label, hint, checked, onChange, disabled }: {
  label: string; hint?: ReactNode; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean;
}) {
  return (
    <label className="ui-check">
      <input type="checkbox" role="switch" className="ui-switch" checked={checked} disabled={disabled}
             onChange={(e) => onChange(e.target.checked)} />
      <span><strong>{label}</strong>{hint ? <><br /><span className="ui-hint">{hint}</span></> : null}</span>
    </label>
  );
}

/** Side panel for details (documents, clients): keeps the list in view. Esc or the scrim closes it. */
export function Drawer({ open, title, onClose, children, width, actions }: {
  open: boolean; title: ReactNode; onClose: () => void; children: ReactNode; width?: number; actions?: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="ui-drawer" role="dialog" aria-modal="true" aria-label={typeof title === "string" ? title : undefined}>
      <div className="ui-drawer-scrim" onClick={onClose} />
      <div className="ui-drawer-panel" style={width ? ({ "--drawer-w": `${width}px` } as React.CSSProperties) : undefined}>
        <header>
          <h2>{title}</h2>
          {actions}
          <Button variant="ghost" className="ui-icon-btn" onClick={onClose} aria-label="Close"><Icon name="close" size={18} /></Button>
        </header>
        <div className="ui-drawer-body">{children}</div>
      </div>
    </div>
  );
}

export function EmptyState({ icon = "inbox", title, children, action }: {
  icon?: IconName; title: string; children?: ReactNode; action?: ReactNode;
}) {
  return (
    <div className="ui-empty-state">
      <span className="ui-empty-icon"><Icon name={icon} size={24} /></span>
      <strong>{title}</strong>
      {children ? <p>{children}</p> : null}
      {action}
    </div>
  );
}

export function Skeleton({ height = 14, width = "100%", style }: { height?: number; width?: number | string; style?: React.CSSProperties }) {
  return <span className="ui-skeleton" style={{ display: "block", height, width, ...style }} aria-hidden="true" />;
}

/** Rows of grey bars while a table loads (keeps the layout from jumping). */
export function SkeletonRows({ rows = 5 }: { rows?: number }) {
  return (
    <div style={{ display: "grid", gap: 14, padding: "6px 0" }} role="status" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => <Skeleton key={i} height={18} width={`${92 - (i % 3) * 14}%`} />)}
    </div>
  );
}

/** Circular score, e.g. return readiness. */
export function Ring({ value, size = 44, stroke = 5, label }: { value: number; size?: number; stroke?: number; label?: ReactNode }) {
  const pct = Math.max(0, Math.min(100, Math.round(value)));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const tone = pct >= 90 ? "var(--green)" : pct >= 60 ? "var(--brand)" : pct >= 35 ? "var(--accent)" : "var(--red)";
  return (
    <span className="ui-ring" style={{ width: size, height: size }} role="img" aria-label={`${pct}%`}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--sunken)" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={tone} strokeWidth={stroke} strokeLinecap="round"
                strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)} />
      </svg>
      <b style={{ fontSize: size * 0.3 }}>{label ?? pct}</b>
    </span>
  );
}

export function Meter({ value, label }: { value: number; label?: ReactNode }) {
  return <div className="ui-meter"><Bar value={value} /><span>{label ?? `${Math.round(value * 100)}%`}</span></div>;
}

export function Hero({ eyebrow, title, children, actions }: { eyebrow?: ReactNode; title: ReactNode; children?: ReactNode; actions?: ReactNode }) {
  return (
    <section className="ui-hero">
      <div className="ui-hero-row">
        <div>
          {eyebrow ? <span className="ui-eyebrow" style={{ color: "var(--accent)" }}>{eyebrow}</span> : null}
          <h1>{title}</h1>
          {children ? <p>{children}</p> : null}
        </div>
        {actions ? <div className="ui-row">{actions}</div> : null}
      </div>
    </section>
  );
}

export interface Step { key: string; title: string; hint?: ReactNode; done: boolean; action?: ReactNode }
export function Steps({ steps }: { steps: Step[] }) {
  return (
    <div className="ui-steps">
      {steps.map((s, i) => (
        <div key={s.key} className={`ui-step ${s.done ? "ui-step-done" : ""}`}>
          <span className="ui-step-mark">{s.done ? <Icon name="check" size={14} /> : i + 1}</span>
          <div className="ui-step-body">
            <div className="ui-step-title">{s.title}</div>
            {s.hint && !s.done ? <div className="ui-hint">{s.hint}</div> : null}
          </div>
          {!s.done ? s.action : null}
        </div>
      ))}
    </div>
  );
}

/** Recognisable marks for the channels a firm connects (simple glyphs in each service's colours). */
export function ChannelLogo({ kind, size = 24 }: { kind: string; size?: number }) {
  const g: Record<string, ReactNode> = {
    whatsapp: <><circle cx="12" cy="12" r="10" fill="#25D366" /><path d="M7.4 16.9l.7-2.5a5.2 5.2 0 1 1 1.9 1.8z" fill="#fff" /><path d="M10.4 9.4c.2-.4.4-.4.6-.4h.4c.1 0 .3 0 .4.3l.6 1.4c0 .1.1.3 0 .4l-.4.5c-.1.1-.1.2 0 .4.4.7 1 1.3 1.8 1.7.1.1.3.1.4-.1l.5-.6c.1-.1.3-.2.4-.1l1.4.7c.2.1.3.2.3.3 0 .4-.1.9-.6 1.2-.4.3-1.2.4-2.2 0a6.8 6.8 0 0 1-3.6-3.4c-.4-.9-.2-1.7 0-2.3z" fill="#25D366" /></>,
    gdrive: <><path d="M8.6 3h6.8l6.3 10.9h-6.8z" fill="#FFBA00" /><path d="M2.3 13.9 5.7 20h12.6l-3.4-6.1z" fill="#2684FC" /><path d="M8.6 3 2.3 13.9 5.7 20l6.3-10.9z" fill="#00AC47" /></>,
    onedrive: <path d="M9.5 8.2a5 5 0 0 1 9 2.4A3.6 3.6 0 0 1 18 17.8H6.4a3.9 3.9 0 0 1-.6-7.8 4.4 4.4 0 0 1 3.7-1.8z" fill="#0A6CD8" />,
    zoho_books: <><rect x="2" y="2" width="20" height="20" rx="5" fill="#E42527" /><path d="M8 8h8l-8 8h8" stroke="#fff" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></>,
    tally: <><rect x="2" y="2" width="20" height="20" rx="5" fill="#1F4E9C" /><path d="M7 8h10M12 8v9" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" /></>,
    email_inbound: <><rect x="2" y="4" width="20" height="16" rx="3" fill="#5B6BF5" /><path d="m4 7 8 6 8-6" stroke="#fff" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" /></>,
    gsp: <><rect x="2" y="2" width="20" height="20" rx="5" fill="#fff" stroke="#E5E7EB" /><path d="M2 8h20" stroke="#F2A03D" strokeWidth="3" /><path d="M2 16h20" stroke="#0E6B4F" strokeWidth="3" /><circle cx="12" cy="12" r="2.2" fill="#1F4E9C" /></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">{g[kind] ?? <><rect x="2" y="2" width="20" height="20" rx="5" fill="var(--sunken)" /></>}</svg>
  );
}

export function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <Button variant="ghost" onClick={async () => {
      try { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); } catch { /* clipboard blocked */ }
    }}>
      <Icon name={done ? "check" : "copy"} size={16} />{done ? "Copied" : label}
    </Button>
  );
}

export function KV({ items }: { items: [string, ReactNode][] }) {
  return (
    <dl className="ui-kv">
      {items.map(([k, v]) => (
        <div key={k}><dt>{k}</dt><dd>{v ?? "—"}</dd></div>
      ))}
    </dl>
  );
}

export function Stat({ label, value, tone, icon, note }: { label: string; value: ReactNode; tone?: string; icon?: IconName; note?: ReactNode }) {
  return (
    <div className={`ui-stat ${tone ? `ui-stat-${tone}` : ""}`}>
      <div className="ui-stat-top">
        <span className="ui-stat-label">{label}</span>
        {icon ? <span className="ui-stat-icon"><Icon name={icon} size={17} /></span> : null}
      </div>
      <span className="ui-stat-value">{value}</span>
      {note ? <span className="ui-stat-note">{note}</span> : null}
    </div>
  );
}

/** Thin progress bar, e.g. accepted share per client. */
export function Bar({ value }: { value: number }) {
  const pct = Math.max(0, Math.min(100, Math.round(value * 100)));
  return <div className="ui-bar" role="img" aria-label={`${pct}%`}><span style={{ width: `${pct}%` }} /></div>;
}

/* ---------- icons (24-px grid, 1.8 stroke; shapes after Lucide, ISC licence) ---------- */
const ICONS = {
  home: <><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V20a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V9.5" /></>,
  inbox: <><path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></>,
  file: <><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5z" /><path d="M14 2v6h6" /><path d="M8 13h8M8 17h5" /></>,
  folder: <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2z" />,
  user: <><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" /></>,
  code: <><path d="m16 18 6-6-6-6" /><path d="m8 6-6 6 6 6" /></>,
  users: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></>,
  shield: <><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" /><path d="m12 8v4M12 16h.01" /></>,
  message: <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22z" />,
  plug: <><path d="M12 22v-5M9 8V2M15 8V2" /><path d="M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8z" /></>,
  mirror: <><rect x="3" y="3" width="8" height="8" rx="2" /><rect x="13" y="13" width="8" height="8" rx="2" /><path d="M7 15v2a2 2 0 0 0 2 2h2M17 9V7a2 2 0 0 0-2-2h-2" /></>,
  activity: <path d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2" />,
  settings: <><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" /><circle cx="12" cy="12" r="3" /></>,
  logout: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="m16 17 5-5-5-5M21 12H9" /></>,
  check: <path d="M20 6 9 17l-5-5" />,
  checkCircle: <><circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" /></>,
  alert: <><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></>,
  info: <><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></>,
  close: <path d="M18 6 6 18M6 6l12 12" />,
  copy: <><rect x="8" y="8" width="14" height="14" rx="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" /></>,
  menu: <path d="M4 6h16M4 12h16M4 18h16" />,
  image: <><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-3.09-3.09a2 2 0 0 0-2.82 0L6 21" /></>,
  download: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="m7 10 5 5 5-5M12 15V3" /></>,
  zap: <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />,
  monitor: <><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></>,
  lock: <><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>,
  chevron: <path d="m9 18 6-6-6-6" />,
  search: <><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></>,
  plus: <path d="M12 5v14M5 12h14" />,
  building: <><rect x="4" y="2" width="16" height="20" rx="2" /><path d="M9 22v-4h6v4M8 6h.01M16 6h.01M12 6h.01M12 10h.01M12 14h.01M16 10h.01M16 14h.01M8 10h.01M8 14h.01" /></>,
  book: <><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" /><path d="M9 7h6" /></>,
  key: <><circle cx="7.5" cy="15.5" r="5.5" /><path d="m21 2-9.6 9.6" /><path d="m15.5 7.5 3 3L22 7l-3-3" /></>,
  link: <><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></>,
  mail: <><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 6L2 7" /></>,
  calendar: <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>,
  filter: <path d="M22 3H2l8 9.46V19l4 2v-8.54z" />,
  more: <><circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" /><circle cx="5" cy="12" r="1" /></>,
  arrowRight: <path d="M5 12h14M12 5l7 7-7 7" />,
  external: <><path d="M15 3h6v6M10 14 21 3" /><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /></>,
  refresh: <><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M21 3v5h-5M21 12a9 9 0 0 1-15 6.7L3 16" /><path d="M8 16H3v5" /></>,
  upload: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="m17 8-5-5-5 5M12 3v12" /></>,
  eye: <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></>,
  sparkles: <><path d="M12 3l1.9 5.6L19.5 10l-5.6 1.9L12 17.5l-1.9-5.6L4.5 10l5.6-1.4z" /><path d="M19 3v4M21 5h-4M5 17v4M7 19H3" /></>,
  rupee: <path d="M6 3h12M6 8h12M6 13l8.5 8M6 13h3a6 6 0 0 0 0-10" />,
  chart: <><path d="M3 3v18h18" /><path d="m19 9-5 5-4-4-3 3" /></>,
  bell: <><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /></>,
  help: <><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01" /></>,
  grid: <><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /></>,
  send: <><path d="M22 2 11 13" /><path d="M22 2 15 22l-4-9-9-4z" /></>,
  mic: <><rect x="9" y="2" width="6" height="12" rx="3" /><path d="M19 10v1a7 7 0 0 1-14 0v-1M12 18v4" /></>,
  layers: <><path d="m12 2 10 5-10 5L2 7z" /><path d="m2 17 10 5 10-5M2 12l10 5 10-5" /></>,
  clock: <><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></>,
  receipt: <><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1z" /><path d="M8 7h8M8 11h8M8 15h5" /></>,
  phone: <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />,
} as const;
export type IconName = keyof typeof ICONS;

export function Icon({ name, size = 20 }: { name: IconName; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round"
         strokeLinejoin="round" aria-hidden="true">{ICONS[name]}</svg>
  );
}

/** The AawazAI tile (brand kit logo/svg/aawaz-icon-green.svg). */
export function LogoMark({ size = 32, onDark }: { size?: number; onDark?: boolean }) {
  // on the green brand panel the tile turns white (brand kit aawaz-icon-white): never green on green
  return (
    <svg width={size} height={size} viewBox="0 0 512 512" role="img" aria-label="AawazAI">
      <rect width="512" height="512" rx="115" fill={onDark ? "#FFFFFF" : "#0E6B4F"} />
      <rect x="110.08" y="116.68" width="291.84" height="224.22" rx="76.2" fill="#F2A03D" />
      <path d="M168.45 323.63 L226.8 335.13 L181.6 404.1 Z" fill="#F2A03D" />
      <rect x="182.46" y="190.68" width="28.6" height="76.2" rx="14.3" fill="#0E6B4F" />
      <rect x="241.7" y="165.98" width="28.6" height="125.55" rx="14.3" fill="#0E6B4F" />
      <rect x="300.94" y="190.68" width="28.6" height="76.2" rx="14.3" fill="#0E6B4F" />
    </svg>
  );
}

export function Logo({ product, size = 32, onDark }: { product: string; size?: number; onDark?: boolean }) {
  return (
    <span className="ui-logo"><LogoMark size={size} onDark={onDark} /><span>AawazAI<small>{product}</small></span></span>
  );
}

export function initials(name: string | null | undefined): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "·";
  return ((parts[0]![0] ?? "") + (parts.length > 1 ? parts[parts.length - 1]![0] ?? "" : "")).toUpperCase();
}

export interface NavItem { to: string; label: string; badge?: number; icon?: IconName; section?: string }

/** App shell: sidebar on desktop, collapsible top bar on phones. `renderLink` lets apps use their router. */
export function Shell({ brand, nav, renderLink, footer, children, firm, top }: {
  brand: ReactNode; nav: NavItem[]; renderLink: (item: NavItem, close: () => void) => ReactNode; footer?: ReactNode;
  children: ReactNode; firm?: ReactNode; top?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  let section: string | undefined;
  return (
    <div className="ui-shell">
      <a href="#main" className="ui-skip">Skip to content</a>
      <aside className={open ? "ui-side ui-side-open" : "ui-side"}>
        <div className="ui-brand">
          {brand}
          <button className="ui-burger" aria-label="Menu" aria-expanded={open} onClick={() => setOpen(!open)}>
            <Icon name={open ? "close" : "menu"} size={20} />
          </button>
        </div>
        {firm}
        <nav aria-label="Main">
          {nav.map((n) => {
            const label = n.section && n.section !== section ? n.section : null;
            section = n.section ?? section;
            return (
              <div key={n.to}>
                {label ? <div className="ui-nav-label">{label}</div> : null}
                {renderLink(n, () => setOpen(false))}
              </div>
            );
          })}
        </nav>
        {footer ? <div className="ui-side-foot">{footer}</div> : null}
      </aside>
      <div className="ui-body">
        {top ? <div className="ui-top">{top}</div> : null}
        <main id="main" className="ui-main">{children}</main>
      </div>
    </div>
  );
}

/** Nav link content: icon, label, optional count. */
export function NavContent({ item }: { item: NavItem }) {
  return (
    <>
      {item.icon ? <Icon name={item.icon} size={18} /> : null}
      <span>{item.label}</span>
      {item.badge ? <span className="ui-nav-count">{item.badge}</span> : null}
    </>
  );
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

/** "2026-10" → "Oct 2026". */
export function fmtPeriod(period: string | null | undefined): string {
  if (!period) return "—";
  const [y, m] = period.split("-").map(Number);
  if (!y || !m) return period;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString("en-GB", { month: "short", year: "numeric", timeZone: "UTC" });
}

const SOURCES: Record<string, string> = {
  whatsapp: "WhatsApp", email_inbound: "Email", gdrive: "Google Drive", onedrive: "OneDrive", tally: "Tally",
  gsp: "GST portal", consumer_upload: "Upload", filesystem: "Folder", zoho_books: "Zoho Books", mcp_upload: "AawazAI CA",
};
export function sourceLabel(s: string | null | undefined): string {
  return s ? SOURCES[s] ?? humanize(s) : "—";
}

export function fmtBytes(n: number | null | undefined): string {
  if (n == null) return "—";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i ? 1 : 0)} ${u[i]}`;
}

export function maskPhone(p: string | null | undefined): string {
  if (!p) return "—";
  return p.length > 4 ? `${p.slice(0, 3)}${"X".repeat(Math.max(0, p.length - 7))}${p.slice(-4)}` : p;
}
